import { useLiveQuery, useStore, useServices } from "@hq/client-react";
import { DragDropRoot, moveItems, useDroppable, useSortable, restoreDragFocus } from "@z/dnd";
import type { Priority, Task, TaskStatus } from "@hq/contracts";
import type { NowContext } from "@hq/core";
import {
  Button,
  Checkbox,
  cn,
  ContextMenu,
  RenameInput,
  DropdownMenu,
  IconButton,
  toast,
} from "@z/ui";
import {
  CalendarDays,
  Circle,
  CircleCheck,
  Clock,
  Ellipsis,
  Flag,
  Folder,
  Layers,
  Moon,
} from "lucide-react";
import { type ReactNode, useState, useRef } from "react";
import { TaskActionItems } from "./rowActions";
import { TaskTitleButton } from "./TaskTitleButton";
import { formatDueDate, formatScheduled } from "./format";
import { useProjectIndex } from "./useProjectNames";

const STATUS_COLUMNS = [
  { status: "open", label: "Open", icon: Circle, color: "text-accent" },
  { status: "someday", label: "Someday", icon: Moon, color: "text-nav-purple" },
  { status: "completed", label: "Completed", icon: CircleCheck, color: "text-success" },
] as const;
const PRIORITY_COLUMNS = [
  { status: "none", label: "No Priority", icon: Flag, color: "text-fg-muted" },
  { status: "low", label: "Low Priority", icon: Flag, color: "text-cal-yellow-fg" },
  { status: "medium", label: "Medium Priority", icon: Flag, color: "text-cal-orange-fg" },
  { status: "high", label: "High Priority", icon: Flag, color: "text-danger" },
] as const;
type Columns = Record<string, Task[]>;

export function TaskBoard({
  tasks,
  now,
  label,
  quickAdd,
  onOpenTask,
}: {
  tasks: readonly Task[];
  now: NowContext;
  label: string;
  quickAdd?: ReactNode;
  onOpenTask?: ((id: string) => void) | undefined;
}) {
  const services = useServices();
  const store = useStore();
  const { names } = useProjectIndex();
  const { data: savedGrouping, isLoading } = useLiveQuery(
    ["kv"],
    (s) => s.kv.get<string>("tasks.boardGrouping"),
    [],
  );
  const grouping = savedGrouping === "status" ? "status" : "priority";
  const definitions = grouping === "status" ? STATUS_COLUMNS : PRIORITY_COLUMNS;
  const columnFor = (task: Task) =>
    grouping === "status" ? task.status : (task.priority ?? "none");
  const boardRef = useRef<HTMLDivElement>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const [preview, setPreview] = useState<Columns | null>(null);
  const [saving, setSaving] = useState(false);
  const columns: Columns = Object.fromEntries(definitions.map((column) => [column.status, []]));
  for (const task of [...tasks].sort((a, b) => a.sortOrder - b.sortOrder)) {
    columns[columnFor(task)]?.push(task);
  }
  const [announcement, setAnnouncement] = useState("");
  const move = async (id: string, status: string, before?: string) => {
    const task = tasks.find((t) => t.id === id);
    if (!task || id === before) return;
    const destination = tasks
      .filter((t) => columnFor(t) === status && t.id !== id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const index = before ? destination.findIndex((t) => t.id === before) : destination.length;
    const next = destination[index]?.sortOrder;
    const previous = destination[index - 1]?.sortOrder;
    let sortOrder =
      next === undefined
        ? (previous ?? 0) + 1024
        : previous === undefined
          ? next - 1024
          : (previous + next) / 2;
    try {
      // Imported or simultaneously created cards can have equal ranks. Restore gaps
      // before inserting between them, keeping their current relative order.
      if (
        next !== undefined &&
        previous !== undefined &&
        (sortOrder <= previous || sortOrder >= next)
      ) {
        for (const [position, card] of destination.entries()) {
          await services.tasks.reorder(card.id, (position + 1) * 1024);
        }
        sortOrder = index * 1024 + 512;
      }
      if (columnFor(task) !== status) {
        if (grouping === "status") await services.tasks.setStatus(id, status as TaskStatus);
        else await services.tasks.setPriority(id, status === "none" ? null : (status as Priority));
      }
      await services.tasks.reorder(id, sortOrder);
      setAnnouncement(
        `Moved ${task.title} to ${definitions.find((c) => c.status === status)?.label}.`,
      );
    } catch {
      toast({ title: "Could not move task", description: "Please try again.", variant: "danger" });
    }
  };
  if (isLoading)
    return (
      <div
        role="status"
        className="h-64 animate-pulse rounded-lg bg-surface motion-reduce:animate-none"
        aria-label="Loading board"
      />
    );
  return (
    <DragDropRoot
      onBeforeDragStart={(event) => {
        if (saving) event.preventDefault();
      }}
      onDragStart={({ operation }) => {
        setDragged(String(operation.source?.id));
        setPreview(columns);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setPreview((current) => moveItems(current ?? columns, event));
      }}
      onDragEnd={async (event) => {
        setDragged(null);
        const id = String(event.operation.source?.id);
        const restoreFocus = () => {
          if (event.nativeEvent instanceof KeyboardEvent) restoreDragFocus(boardRef.current, id);
        };
        if (event.canceled || !event.operation.target || !preview) {
          setPreview(null);
          restoreFocus();
          return;
        }
        const destination = definitions.find(({ status }) =>
          preview[status]?.some((task) => task.id === id),
        );
        if (destination) {
          const cards = preview[destination.status] ?? [];
          const index = cards.findIndex((task) => task.id === id);
          const original = columns[destination.status] ?? [];
          if (original[index]?.id !== id) {
            setSaving(true);
            await move(id, destination.status, cards[index + 1]?.id);
            setSaving(false);
          }
        }
        setPreview(null);
        restoreFocus();
      }}
    >
      <div
        ref={boardRef}
        tabIndex={-1}
        data-slot="task-board"
        aria-label={`${label} board`}
        className="flex min-h-0 flex-col gap-4"
      >
        {quickAdd}
        <div className="flex items-center justify-between gap-3">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              render={
                <Button variant="ghost" size="sm" aria-label="Board grouping">
                  <Layers />
                  Group by {grouping === "priority" ? "Priority" : "Status"}
                </Button>
              }
            />
            <DropdownMenu.Content align="start">
              {(["priority", "status"] as const).map((value) => (
                <DropdownMenu.CheckboxItem
                  key={value}
                  checked={grouping === value}
                  disabled={saving || dragged !== null}
                  onCheckedChange={() =>
                    void store.kv
                      .set("tasks.boardGrouping", value)
                      .catch(() =>
                        toast({ title: "Could not save board grouping", variant: "danger" }),
                      )
                  }
                >
                  {value === "priority" ? "Priority" : "Status"}
                </DropdownMenu.CheckboxItem>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
          <span className="text-xs tabular-nums text-fg-muted">
            {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
          </span>
        </div>
        <p className="sr-only">
          Drag a card to move it. Or focus a card, press Space, and use the arrow keys.
        </p>
        <p className="sr-only">Swipe to see all columns. Use a card’s menu to move it.</p>
        <div role="status" className="sr-only">
          {announcement}
        </div>
        <div className="grid auto-cols-[minmax(15rem,1fr)] grid-flow-col items-stretch gap-4 overflow-x-auto pb-4">
          {definitions.map(({ status, label: columnLabel }) => {
            const column = (preview ?? columns)[status] ?? [];
            return (
              <BoardColumn key={status} status={status} label={columnLabel}>
                <h3 className="flex items-center gap-2 px-3 py-3 text-sm font-medium">
                  {columnLabel}
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-md bg-fg/5 px-1 text-xs font-normal tabular-nums text-fg-muted">
                    {column.length}
                  </span>
                </h3>
                <div className="flex flex-col gap-2 px-2 pb-2">
                  {column.map((task, index) => (
                    <BoardCard
                      key={task.id}
                      task={task}
                      index={index}
                      status={status}
                      disabled={saving}
                    >
                      {(onRename) => (
                        <>
                          <div className="flex items-start gap-2">
                            <BoardCompletion task={task} disabled={saving} />
                            <TaskTitleButton
                              onRename={onRename}
                              data-slot="board-task-title"
                              className="min-h-10 min-w-0 flex-1 rounded-sm py-2.5 text-left text-sm font-medium leading-5 [overflow-wrap:anywhere] focus-ring"
                              onOpen={() => onOpenTask?.(task.id)}
                            >
                              {task.title}
                            </TaskTitleButton>
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger
                                render={
                                  <IconButton
                                    className="size-10 text-fg-faint hover:text-fg data-popup-open:bg-fg/6 data-popup-open:text-fg"
                                    label={`Move ${task.title}`}
                                  >
                                    <Ellipsis />
                                  </IconButton>
                                }
                              />
                              <DropdownMenu.Content align="end">
                                <DropdownMenu.Label>Move to</DropdownMenu.Label>
                                {definitions.map((c) => (
                                  <DropdownMenu.Item
                                    key={c.status}
                                    disabled={c.status === status}
                                    onClick={() => void move(task.id, c.status)}
                                  >
                                    <c.icon />
                                    {c.label}
                                  </DropdownMenu.Item>
                                ))}
                              </DropdownMenu.Content>
                            </DropdownMenu.Root>
                          </div>
                          <BoardCardMetadata
                            task={task}
                            now={now}
                            projectName={task.projectId ? names.get(task.projectId) : undefined}
                          />
                        </>
                      )}
                    </BoardCard>
                  ))}
                  {column.length === 0 && (
                    <div
                      className={cn(
                        "flex min-h-24 items-center justify-center px-3 text-xs text-fg-faint",
                        dragged && "border-accent/50 text-accent",
                      )}
                    >
                      {dragged ? "Drop task here" : <span className="sr-only">No tasks</span>}
                    </div>
                  )}
                </div>
              </BoardColumn>
            );
          })}
        </div>
      </div>
    </DragDropRoot>
  );
}

function BoardCardMetadata({
  task,
  now,
  projectName,
}: {
  task: Task;
  now: NowContext;
  projectName: string | undefined;
}) {
  return (
    <>
      {task.description && (
        <p className="mb-2 line-clamp-2 text-xs leading-5 text-fg-muted [overflow-wrap:anywhere]">
          {task.description}
        </p>
      )}
      {projectName && (
        <p className="mb-2 flex min-w-0 items-center gap-1.5 text-xs text-fg-muted">
          <Folder aria-hidden="true" className="size-3 shrink-0" />
          <span className="truncate">{projectName}</span>
        </p>
      )}
      {(task.dueDate ||
        task.plannedDate ||
        task.scheduledAt ||
        task.priority ||
        (task.tags?.length ?? 0) > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 pb-1 text-xs leading-4 text-fg-muted">
          {task.priority && (
            <span
              aria-label={`${task.priority} priority`}
              className={cn(
                "inline-flex max-w-full items-center gap-1 rounded-sm bg-current/5 px-1.5 py-0.5",
                PRIORITY_COLUMNS.find((c) => c.status === task.priority)?.color,
              )}
            >
              <Flag aria-hidden="true" className="size-3 shrink-0" />
              <span className="capitalize">{task.priority}</span>
            </span>
          )}
          {task.dueDate && (
            <span className="inline-flex min-w-0 items-center gap-1 rounded-sm bg-surface px-1.5 py-0.5">
              <CalendarDays aria-hidden="true" className="size-3 shrink-0" />
              <span>Due {formatDueDate(task.dueDate, now)}</span>
            </span>
          )}
          {task.plannedDate && (
            <span className="inline-flex min-w-0 items-center gap-1 rounded-sm bg-surface px-1.5 py-0.5">
              <CalendarDays aria-hidden="true" className="size-3 shrink-0" />
              <span>When {formatDueDate(task.plannedDate, now)}</span>
            </span>
          )}
          {task.scheduledAt && (
            <span className="inline-flex min-w-0 items-center gap-1 rounded-sm bg-surface px-1.5 py-0.5">
              <Clock aria-hidden="true" className="size-3 shrink-0" />
              <span>When {formatScheduled(task.scheduledAt, now)}</span>
            </span>
          )}
          {task.tags?.map((tag) => (
            <span
              key={tag}
              className="min-w-0 max-w-full rounded-sm bg-surface px-1.5 py-0.5 [overflow-wrap:anywhere]"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

function BoardCompletion({ task, disabled }: { task: Task; disabled: boolean }) {
  const services = useServices();
  const [pending, setPending] = useState(false);
  const completed = task.status === "completed";
  const changeStatus = async (status: TaskStatus) => {
    setPending(true);
    try {
      await services.tasks.setStatus(task.id, status);
      toast({
        title: status === "completed" ? "Task completed" : "Task reopened",
        description: task.title,
        action: {
          label: "Undo",
          onClick: () =>
            void services.tasks
              .setStatus(task.id, task.status)
              .catch(() => toast({ title: "Could not undo task status", variant: "danger" })),
        },
      });
    } catch {
      toast({
        title: "Could not update task",
        description: "Please try again.",
        variant: "danger",
      });
    } finally {
      setPending(false);
    }
  };
  return (
    <Checkbox
      variant="task"
      aria-label={completed ? `Reopen “${task.title}”` : `Complete “${task.title}”`}
      checked={completed}
      disabled={disabled || pending}
      className="relative mt-3 before:absolute before:-inset-2 before:content-['']"
      onCheckedChange={() => void changeStatus(completed ? "open" : "completed")}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    />
  );
}

function BoardColumn({
  status,
  label,
  children,
}: {
  status: string;
  label: string;
  children: ReactNode;
}) {
  const { ref, isDropTarget } = useDroppable({
    id: status,
    accept: "task",
    collisionPriority: -1,
    data: { label },
  });
  return (
    <section
      ref={ref}
      aria-label={label}
      className={cn(
        "min-h-[28rem] overflow-hidden rounded-[18px] border border-border/60 bg-surface/70 transition-colors motion-reduce:transition-none",
        isDropTarget && "bg-accent-muted/40 ring-1 ring-inset ring-accent/30",
      )}
    >
      {children}
    </section>
  );
}

function BoardCard({
  task,
  index,
  status,
  disabled,
  children,
}: {
  task: Task;
  index: number;
  status: string;
  disabled: boolean;
  children: (onRename: () => void) => ReactNode;
}) {
  const services = useServices();
  const [renaming, setRenaming] = useState(false);
  const menuHandoff = useRef(false);
  const cardRef = useRef<HTMLElement>(null);
  const { ref, handleRef, isDragSource } = useSortable({
    id: task.id,
    index,
    group: status,
    type: "task",
    accept: "task",
    disabled: disabled || renaming,
    data: { label: task.title },
  });
  return (
    <ContextMenu.Root
      disabled={renaming || disabled}
      onOpenChange={(open) => {
        if (open) menuHandoff.current = false;
      }}
    >
      <ContextMenu.Trigger
        render={
          <article
            ref={(node) => {
              cardRef.current = node;
              // Detach the drag target while editing so its disabled state does
              // not mark the nested rename input as disabled for assistive tech.
              ref(renaming ? null : node);
              handleRef(renaming ? null : node);
            }}
            role="article"
            data-drag-handle={task.id}
            title="Drag to move · Space to pick up"
            onPointerDownCapture={(event) => {
              const target = event.target;
              // Menus and the rename field remain independently interactive.
              if (
                target instanceof Element &&
                target.closest("button, input, textarea, [contenteditable=true]") &&
                !target.closest('[data-slot="board-task-title"]')
              )
                event.stopPropagation();
            }}
            tabIndex={0}
            aria-label={task.title}
            className={cn(
              "relative min-w-0 touch-none cursor-grab rounded-lg border border-border/80 bg-bg px-3 py-2 shadow-sm outline-none transition-[border-color,box-shadow] duration-fast hover:border-border-strong hover:shadow-md focus-visible:ring-2 focus-visible:ring-focus active:cursor-grabbing motion-reduce:transition-none",
              isDragSource && "z-10 border-accent shadow-lg",
            )}
          >
            {renaming ? (
              <RenameInput
                initial={task.title}
                label="Task title"
                onSave={(title) => services.tasks.rename(task.id, title)}
                onClose={() => {
                  setRenaming(false);
                  cardRef.current?.focus();
                }}
              />
            ) : (
              children(() => setRenaming(true))
            )}
          </article>
        }
      />
      <ContextMenu.Content
        aria-label={`Task actions for ${task.title}`}
        finalFocus={() => (menuHandoff.current ? false : (cardRef.current ?? false))}
      >
        <TaskActionItems
          task={task}
          onRename={() => {
            menuHandoff.current = true;
            requestAnimationFrame(() => setRenaming(true));
          }}
        />
      </ContextMenu.Content>
    </ContextMenu.Root>
  );
}
